import { ReactNode, useState } from 'react';

import { ContentSwitcher } from '@iwoca/orion';

import {
  CancelledPlan,
  CapturedPayment,
  EndedPaymentPlan,
  OngoingPaymentPlan,
  Plan,
  PlanTypes,
} from '../../Dashboard.types';
import { PaymentPlanCard } from '../PaymentPlanCard/PaymentPlanCard';
import { PlanButton } from '../PlanButton/PlanButton';

export const PaymentPlans = ({
  endedPayments,
  ongoingPaymentPlans,
  capturedPlans,
  cancelledPayments,
  selectedPlan,
  selectPaymentPlan,
  renderContent,
}: {
  endedPayments: EndedPaymentPlan[];
  ongoingPaymentPlans: OngoingPaymentPlan[];
  capturedPlans: CapturedPayment[];
  cancelledPayments: CancelledPlan[];
  selectedPlan: Plan | null;
  selectPaymentPlan: (plan: Plan | null) => void;
  renderContent?: () => ReactNode;
}) => {
  const defaultOption = (): PlanTypes => {
    if (capturedPlans.length > 0) return 'pending';
    if (ongoingPaymentPlans.length > 0) return 'active';
    if (endedPayments.length > 0 || cancelledPayments.length > 0)
      return 'completed';

    return 'active';
  };

  const [selectedPlanType, setSelectedPlanType] =
    useState<PlanTypes>(defaultOption);

  const getPaymentPlans = (): NonNullable<Plan>[] => {
    switch (selectedPlanType) {
      case 'active':
        return ongoingPaymentPlans;
      case 'pending':
        return capturedPlans;
      case 'completed':
        return [...endedPayments, ...cancelledPayments];
    }
  };

  if (
    !endedPayments.length &&
    !ongoingPaymentPlans.length &&
    !capturedPlans.length &&
    !cancelledPayments.length
  )
    return null;

  return (
    <>
      <div>
        <ContentSwitcher
          value={selectedPlanType}
          className="!w-full m:!h-max m:!w-full m:!justify-between"
        >
          {capturedPlans.length > 0 && (
            <ContentSwitcher.Button
              key={'pending'}
              id={'pending'}
              className="!flex flex-1 justify-center"
              onClick={() => setSelectedPlanType('pending')}
            >
              Pending
            </ContentSwitcher.Button>
          )}
          <ContentSwitcher.Button
            key={'active'}
            id={'active'}
            className="!flex flex-1 justify-center"
            onClick={() => setSelectedPlanType('active')}
          >
            Active
          </ContentSwitcher.Button>
          <ContentSwitcher.Button
            key={'completed'}
            id={'completed'}
            className="!flex flex-1 justify-center"
            onClick={() => setSelectedPlanType('completed')}
          >
            Completed
          </ContentSwitcher.Button>
        </ContentSwitcher>
      </div>
      <h2 className="mb-m mt-l text-l font-bold">
        Your {selectedPlanType.toLowerCase()} payment plans
      </h2>
      <ul className="m-0 flex list-none flex-col gap-s p-0">
        {getPaymentPlans().map((paymentPlan) => (
          <li key={paymentPlan.pay_link_id}>
            <PlanButton
              selectedPlanId={selectedPlan?.pay_link_id ?? null}
              selectPaymentPlan={selectPaymentPlan}
              paymentPlan={paymentPlan}
              status={paymentPlan.status}
              renderContent={renderContent}
            >
              <PaymentPlanCard
                created={paymentPlan.payLink.created}
                funded_at={paymentPlan.payLink.funded_at}
                reference={paymentPlan.payLink.reference}
                sellerName={paymentPlan.payLink.seller_trading_name}
                amount={paymentPlan.payLink.amount}
                status={paymentPlan.status}
              />
            </PlanButton>
          </li>
        ))}
      </ul>
    </>
  );
};
